import type { ReactNode } from 'react';
import { useEffect, useRef } from 'react';

import { useStore } from '@nanostores/react';
import { useQuery } from '@tanstack/react-query';

import { myIdentificationQueryDefinition } from '@/entities/identification';
import { myEmployeeRecordQueryDefinition } from '@/entities/me';
import { sessionStore } from '@/entities/session';
import { analytics, setUser } from '@/shared';

import type { SessionStatus } from '@/entities/session/model/types';

interface AnalyticsProviderProps {
  children: ReactNode;
}

export function AnalyticsProvider({ children }: AnalyticsProviderProps) {
  const session = useStore(sessionStore);
  const isAuthenticated = session.status === 'authenticated';

  const identificationQuery = useQuery({
    ...myIdentificationQueryDefinition(),
    enabled: isAuthenticated,
  });

  const employeeRecordQuery = useQuery({
    ...myEmployeeRecordQueryDefinition(),
    enabled: isAuthenticated,
  });

  const lastStatusRef = useRef<SessionStatus | null>(null);
  const lastUserIdRef = useRef<string | null>(null);

  useEffect(() => {
    void analytics.init();
  }, []);

  useEffect(() => {
    const backendUserId = identificationQuery.data?.userId;
    if (backendUserId === undefined) {
      return;
    }

    const displayName = employeeRecordQuery.data?.displayName ?? undefined;

    if (lastUserIdRef.current !== backendUserId) {
      lastUserIdRef.current = backendUserId;
      const firebaseUserId = session.user?.id;
      if (firebaseUserId !== undefined) {
        setUser({
          firebaseUserId,
          backendUserId,
          phoneNumber: session.user?.phoneNumber,
        });
      }
      void analytics.identify({
        userId: backendUserId,
        traits: {
          isAnonymous: session.user?.isAnonymous ?? false,
          hasPhoneNumber: Boolean(session.user?.phoneNumber),
          displayName,
        },
      });
    }
  }, [identificationQuery.data, employeeRecordQuery.data, session.user]);

  useEffect(() => {
    if (session.status === 'unauthenticated' && lastStatusRef.current === 'authenticated') {
      lastUserIdRef.current = null;
      void analytics.reset();
    }

    lastStatusRef.current = session.status;
  }, [session.status]);

  return children;
}
